import { useEffect, useState } from 'react'
import { Button, useTheme } from '@itsrever/design-system'
import { useNavigate } from 'react-router'
import clsx from 'clsx'
import styled from 'styled-components'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
    RefundActions,
    ReturnMethod,
    setPickUpRanges
} from '@/redux/return/returnSlice'
import { PkgInfrastructureRestReturnMethod as RestReturnMethod } from '@itsrever/returns-api-types'
import { device, disabledForm } from '@/utils'
import { useCalculate, useTranslate, usePages, useReturnMethods } from '@/hooks'
import ReturnCard from '@/components/ReturnCard/ReturnCard'
import TopBar from '@/components/TopBarComponent/TopBar'
import { ReverCard } from '@/components/ReverCard'
import FallbackNotFound from '@/components/FallbackNotFound'
import { LoadingModal } from '@/components/Loading'
import { useFeatureFlag } from '@/application/use-feature-flag'
import HoffTopBar from '@/components/HoffTopBar/hoff-top-bar'

function getFinalReturnMethods(returnMethods: RestReturnMethod[]) {
    const checker = new Map<number, boolean>()
    const availableReturnMethods: RestReturnMethod[] = []
    returnMethods.forEach((retMethod) => {
        if (retMethod.name) {
            if (!checker.has(retMethod.name)) {
                checker.set(retMethod.name, true)
                availableReturnMethods.push(retMethod)
            }
        }
    })
    return availableReturnMethods
}

const PICKUP_RANGES_FLAG_ID = 1717

function ReturnMethodPage() {
    const { translate } = useTranslate()
    const navigate = useNavigate()
    const theme = useTheme()
    const dispatch = useAppDispatch()
    const { pages } = usePages()

    const settings = useAppSelector(
        (store) => store.settingsApi.settings.response
    )

    const { isFlagEnabled } = useFeatureFlag(PICKUP_RANGES_FLAG_ID)
    const shouldHidePickupRanges = isFlagEnabled

    const addressObject = useAppSelector((store) => store.return.returnAddress)

    const ret = useAppSelector((store) => store.return)
    const returnMethod = ret.returnMethod

    const displayPickupRanges =
        !shouldHidePickupRanges && returnMethod === ReturnMethod.HomePickup

    const returnMethods = useAppSelector(
        (store) => store.returnMethodsApi.returnMethods.response.return_methods
    )

    //timepicker // 3 Time Ranges
    const [time, setTime] = useState([
        { timeRange: 0, selected: false, calcString: 1 },
        { timeRange: 1, selected: false, calcString: 2 },
        { timeRange: 2, selected: false, calcString: 3 }
    ])
    const [pickupRangesState, setPickupRanges] = useState<number[]>([])

    const handleTime = (timeRange: number) => {
        setTime(
            time.map((item) =>
                item.timeRange === timeRange
                    ? { ...item, selected: !item.selected }
                    : item
            )
        )
    }

    useEffect(() => {
        const pickupRanges = time.filter((item) => item.selected)
        setPickupRanges(pickupRanges.map((item) => item.calcString))
        dispatch(setPickUpRanges(pickupRanges.map((item) => item.calcString)))
    }, [time])

    const availableReturnMethods = getFinalReturnMethods(returnMethods ?? [])

    const { callCalculate, loadingCalculate } = useCalculate()
    const { loadingReturnMethods } = useReturnMethods()
    const hasExchanges = ret.returnedItems.some(
        (item) => item.item.action === RefundActions.ToExchange
    )

    const onClickContinue = () => {
        if (hasExchanges) {
            if (returnMethod === ReturnMethod.DeliveryPickup) {
                callCalculate(returnMethod, addressObject)

                return
            }

            navigate(pages.ExchangeAddress)
        } else {
            callCalculate(returnMethod)
        }
    }

    const disableContinue =
        availableReturnMethods.length == 0 ||
        disabledForm(
            addressObject,
            0,
            undefined,
            pickupRangesState,
            returnMethod,
            displayPickupRanges
        )

    const isHoff = settings.ecommerce_group == 'hoff'

    if (isHoff) {
        return (
            <>
                {loadingCalculate || loadingReturnMethods ? (
                    <LoadingModal loading />
                ) : null}
                <TopBar page="return-method" />
                <div className="relative mx-auto flex size-full max-w-[1000px] grow flex-col items-center bg-cover bg-center bg-no-repeat px-4 md:px-10">
                    <HoffTopBar />
                    <div className="size-full">
                        <p
                            className="mb-5 mt-4 text-center font-haas text-lg font-normal text-neutral-800 md:mt-0"
                            dangerouslySetInnerHTML={{
                                __html: translate('return_page_title')
                            }}
                        />
                        {availableReturnMethods.length > 0 ? (
                            availableReturnMethods.map((retMethod) => {
                                return (
                                    <ReturnCard
                                        key={retMethod.name}
                                        method={retMethod}
                                    />
                                )
                            })
                        ) : (
                            <FallbackNotFound
                                title={translate(
                                    'return_page_no_methods_available'
                                )}
                            />
                        )}

                        {displayPickupRanges ? (
                            <div className="mt-4">
                                <span
                                    className="text-base"
                                    dangerouslySetInnerHTML={{
                                        __html: translate(
                                            'return_page_pickup_times'
                                        )
                                    }}
                                />
                                <div className="mt-4 grid grid-cols-2 gap-4">
                                    <TimeFrame
                                        id="morning"
                                        color={theme.colors.grey[0]}
                                        bgColor="black"
                                        bgText="white"
                                        clicked={time[0].selected}
                                        onClick={() => {
                                            handleTime(0)
                                        }}
                                    >
                                        8:00 - 12:00
                                    </TimeFrame>
                                    <TimeFrame
                                        color={theme.colors.grey[0]}
                                        bgColor="black"
                                        bgText="white"
                                        clicked={time[1].selected}
                                        onClick={() => {
                                            handleTime(1)
                                        }}
                                    >
                                        12:00 - 16:00
                                    </TimeFrame>
                                    <TimeFrame
                                        color={theme.colors.grey[0]}
                                        clicked={time[2].selected}
                                        bgColor="black"
                                        bgText="white"
                                        onClick={() => {
                                            handleTime(2)
                                        }}
                                    >
                                        16:00 - 20:00
                                    </TimeFrame>
                                </div>
                            </div>
                        ) : null}
                        <div className="mt-2 flex w-full justify-center">
                            <button
                                onClick={() => onClickContinue()}
                                id="returnMethodContinue"
                                disabled={disableContinue}
                                className="ml-auto mt-8 w-fit min-w-44 cursor-pointer rounded-xl bg-black p-4 font-haas text-sm text-white disabled:cursor-not-allowed disabled:bg-neutral-500"
                            >
                                {translate('return_page_button_continue')}
                            </button>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    const logoUrl = settings.branding?.logo_url
    const title = settings.branding?.title
    const backgroundUrl = settings.branding?.background_image_url

    const isMisako = settings.ecommerce_group == 'misako'

    if (isMisako) {
        return (
            <>
                {loadingCalculate || loadingReturnMethods ? (
                    <LoadingModal loading />
                ) : null}
                <div className="relative grid grid-cols-1 md:grid-cols-2">
                    <div
                        className={clsx(
                            'sticky top-0 -z-10 h-screen w-full [grid-area:1/1] md:z-0 md:[grid-area:1/2]',
                            {
                                'bg-neutral-200': !backgroundUrl
                            }
                        )}
                    >
                        {backgroundUrl ? (
                            <img
                                src={backgroundUrl}
                                className="size-full object-cover"
                                alt=""
                            />
                        ) : null}
                    </div>
                    <div className="flex h-full flex-col justify-center gap-4 bg-white p-4 [grid-area:1/1] md:py-6 md:[grid-area:1/1] lg:px-10">
                        <div className="flex w-full items-end">
                            {logoUrl ? (
                                <div className="mr-auto">
                                    <img
                                        style={
                                            {
                                                '--logo-desktop-height':
                                                    '1.5rem',
                                                '--logo-mobile-height': '1.2rem'
                                            } as React.CSSProperties
                                        }
                                        width={285}
                                        height={70}
                                        src={logoUrl}
                                        alt={title}
                                        className="h-[var(--logo-mobile-height)] w-auto object-contain lg:h-[var(--logo-desktop-height)]"
                                    />
                                </div>
                            ) : null}
                            <div className="flex items-center gap-5">
                                <a
                                    href="https://www.misako.com/pages/wishlist"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <img
                                        src="https://www.misako.com/cdn/shop/files/favorite.svg?v=1731587455"
                                        width={20}
                                        height={20}
                                    />
                                </a>
                                <a
                                    href="https://www.misako.com/account"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <svg
                                        width="20"
                                        height="21"
                                        viewBox="0 0 20 21"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M19 20V17.7778C19 15.3222 16.9863 13.3333 14.5 13.3333H5.5C3.01375 13.3333 1 15.3222 1 17.7778V20"
                                            stroke="rgb(21, 21, 21)"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        ></path>
                                        <path
                                            d="M10 9C12.2091 9 14 7.20914 14 5C14 2.79086 12.2091 1 10 1C7.79086 1 6 2.79086 6 5C6 7.20914 7.79086 9 10 9Z"
                                            stroke="rgb(21, 21, 21)"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        ></path>
                                    </svg>
                                </a>
                                <a
                                    href="https://www.misako.com/collections/top-ventas"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <svg
                                        width="19"
                                        height="19"
                                        viewBox="0 0 19 19"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M13.6969 13.9469L18 18.25M15.875 8.6875C15.875 12.7951 12.5451 16.125 8.4375 16.125C4.32988 16.125 1 12.7951 1 8.6875C1 4.57988 4.32988 1.25 8.4375 1.25C12.5451 1.25 15.875 4.57988 15.875 8.6875Z"
                                            stroke="rgb(21, 21, 21)"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        ></path>
                                    </svg>
                                </a>
                            </div>
                        </div>
                        <div className="flex h-fit flex-1 flex-col bg-white">
                            <h1
                                className="mb-9 mt-5 text-lg font-[300] tracking-wider text-neutral-600 [font-family:Inter] lg:text-xl"
                                dangerouslySetInnerHTML={{
                                    __html: translate('return_page_title')
                                }}
                            />
                            <div className="mb-6 flex flex-col flex-wrap justify-stretch gap-4 xl:flex-row">
                                {availableReturnMethods.length > 0 ? (
                                    availableReturnMethods.map((retMethod) => {
                                        return (
                                            <ReturnCard
                                                key={retMethod.name}
                                                method={retMethod}
                                            />
                                        )
                                    })
                                ) : (
                                    <FallbackNotFound
                                        title={translate(
                                            'return_page_no_methods_available'
                                        )}
                                    />
                                )}
                            </div>
                            {displayPickupRanges ? (
                                <div className="mb-8 mt-4">
                                    <span
                                        className="text-base font-[300] tracking-wider [font-family:Inter]"
                                        dangerouslySetInnerHTML={{
                                            __html: translate(
                                                'return_page_pickup_times'
                                            )
                                        }}
                                    />
                                    <div className="mt-4 flex gap-4 text-sm">
                                        <div
                                            className={clsx(
                                                'cursor-pointer rounded-sm border border-solid border-neutral-300 px-3.5 py-2.5 transition-all [font-family:Inter] hover:border-neutral-800 hover:bg-neutral-800 hover:text-white',
                                                {
                                                    'bg-neutral-800 text-white':
                                                        time[0].selected
                                                }
                                            )}
                                            id="morning"
                                            onClick={() => {
                                                handleTime(0)
                                            }}
                                        >
                                            8:00 - 12:00
                                        </div>
                                        <div
                                            className={clsx(
                                                'cursor-pointer rounded-sm border border-solid border-neutral-300 px-3.5 py-2.5 transition-all [font-family:Inter] hover:border-neutral-800 hover:bg-neutral-800 hover:text-white',
                                                {
                                                    'bg-neutral-800 text-white':
                                                        time[1].selected
                                                }
                                            )}
                                            onClick={() => {
                                                handleTime(1)
                                            }}
                                        >
                                            12:00 - 16:00
                                        </div>
                                        <div
                                            className={clsx(
                                                'cursor-pointer rounded-sm border border-solid border-neutral-300 px-3.5 py-2.5 transition-all [font-family:Inter] hover:border-neutral-800 hover:bg-neutral-800 hover:text-white',
                                                {
                                                    'bg-neutral-800 text-white':
                                                        time[2].selected
                                                }
                                            )}
                                            onClick={() => {
                                                handleTime(2)
                                            }}
                                        >
                                            16:00 - 20:00
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                            <div className="mt-auto flex items-center justify-between gap-4">
                                <div className="w-full md:max-w-[180px]">
                                    <Button
                                        onClick={() => navigate(-1)}
                                        id="returnAddressContinue"
                                        fullWidth
                                        variant="outlined"
                                    >
                                        <span className="text-[13px] font-[300] uppercase tracking-wide [font-family:Inter]">
                                            Volver
                                        </span>
                                    </Button>
                                </div>
                                <div className="w-full md:max-w-[180px]">
                                    <Button
                                        onClick={() => onClickContinue()}
                                        id="returnMethodContinue"
                                        fullWidth
                                        disabled={disableContinue}
                                    >
                                        <span className="text-[13px] font-[300] uppercase tracking-wide [font-family:Inter]">
                                            {translate(
                                                'return_page_button_continue'
                                            )}
                                        </span>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    return (
        <>
            {loadingCalculate || loadingReturnMethods ? (
                <LoadingModal loading />
            ) : null}
            <TopBar page="return-method" />
            <MainDiv>
                <ReverCard>
                    <p
                        className="mb-5 text-center text-lg font-normal text-neutral-800"
                        dangerouslySetInnerHTML={{
                            __html: translate('return_page_title')
                        }}
                    />
                    {availableReturnMethods.length > 0 ? (
                        availableReturnMethods.map((retMethod) => {
                            return (
                                <ReturnCard
                                    key={retMethod.name}
                                    method={retMethod}
                                />
                            )
                        })
                    ) : (
                        <FallbackNotFound
                            title={translate(
                                'return_page_no_methods_available'
                            )}
                        />
                    )}

                    {displayPickupRanges ? (
                        <div className="mt-4">
                            <span
                                className="text-base"
                                dangerouslySetInnerHTML={{
                                    __html: translate(
                                        'return_page_pickup_times'
                                    )
                                }}
                            />
                            <div className="mt-4 grid grid-cols-2 gap-4">
                                <TimeFrame
                                    id="morning"
                                    color={theme.colors.grey[0]}
                                    bgColor="black"
                                    bgText="white"
                                    clicked={time[0].selected}
                                    onClick={() => {
                                        handleTime(0)
                                    }}
                                >
                                    8:00 - 12:00
                                </TimeFrame>
                                <TimeFrame
                                    color={theme.colors.grey[0]}
                                    bgColor="black"
                                    bgText="white"
                                    clicked={time[1].selected}
                                    onClick={() => {
                                        handleTime(1)
                                    }}
                                >
                                    12:00 - 16:00
                                </TimeFrame>
                                <TimeFrame
                                    color={theme.colors.grey[0]}
                                    clicked={time[2].selected}
                                    bgColor="black"
                                    bgText="white"
                                    onClick={() => {
                                        handleTime(2)
                                    }}
                                >
                                    16:00 - 20:00
                                </TimeFrame>
                            </div>
                        </div>
                    ) : null}
                    <div className="mt-2 flex w-full justify-center">
                        <div className="w-full md:max-w-[280px]">
                            <Button
                                onClick={() => onClickContinue()}
                                id="returnMethodContinue"
                                fullWidth
                                disabled={disableContinue}
                            >
                                {translate('return_page_button_continue')}
                            </Button>
                        </div>
                    </div>
                </ReverCard>
            </MainDiv>
        </>
    )
}

const MainDiv = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    align-content: center;
    flex-grow: 1;
    @media ${device.lg} {
        background-color: #f2f2f2;
        flex-direction: row;
        align-items: flex-start;
        justify-content: space-evenly;
    }
`

interface TimeProps {
    bgColor: string
    color: string
    bgText: string
    clicked: boolean
}

const TimeFrame = styled.div<TimeProps>`
    color: ${(p) => (p.color ? p.color : 'black')};
    border: 1px solid;
    border-color: ${(p) => (p.color ? p.color : 'black')};
    background-color: transparent;
    cursor: pointer;
    border-radius: 33px;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    align-content: center;
    align-items: center;
    max-height: 4rem;
    font-size: small;
    @media ${device.sm} {
        font-size: 14;
    }
    background-color: ${(p) => (p.clicked ? p.bgColor : 'transparent')};
    border-color: ${(p) => (p.clicked ? p.bgColor : 'transparent')};
    box-shadow: 0px 1px 4px 1px rgba(0, 0, 0, 0.25);
    color: ${(p) => (p.clicked ? p.bgText : '')};
`

export default ReturnMethodPage
